import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, CombinedTable } from '@/types/table';

import { useResolvedEstablishmentId } from './queries/useResolvedEstablishmentId';
import { useTablesQuery, tablesQueryKey } from './queries/useTablesQuery';
import {
  useCombinedTablesQuery,
  combinedTablesQueryKey,
} from './queries/useCombinedTablesQuery';
import {
  useReservationsQuery,
  reservationsQueryKey,
  computeReservationsDateRange,
} from './queries/useReservationsQuery';
import {
  useAvailabilitiesQuery,
  availabilitiesQueryKey,
} from './queries/useAvailabilitiesQuery';
import {
  useWeekdayAvailabilitiesQuery,
  weekdayAvailabilitiesQueryKey,
} from './queries/useWeekdayAvailabilitiesQuery';
import { useTimeSlotsQuery, timeSlotsQueryKey } from './queries/useTimeSlotsQuery';
import {
  useBookablePeriodsQuery,
  bookablePeriodsQueryKey,
} from './queries/useBookablePeriodsQuery';
import {
  useDateOverridesQuery,
  dateOverridesQueryKey,
} from './queries/useDateOverridesQuery';

interface UseSupabaseDataOptions {
  useAuthenticatedData?: boolean;
  establishmentId?: string | null;
  slug?: string | null;
  session?: Session | null;
  selectedDate?: Date | null;
  pastDaysOverride?: number;
}

/**
 * Façade qui compose 8 hooks React Query dédiés.
 * Contrat de retour identique à l'ancienne version pour ne rien casser.
 *
 * Bénéfices vs ancienne implémentation :
 * - Cache partagé entre pages (Dashboard / Statistics / TableManagement)
 * - Invalidations chirurgicales (1 ressource au lieu de tout refetch)
 * - Retry automatique
 * - refetchOnWindowFocus
 */
export function useSupabaseData(options: UseSupabaseDataOptions = {}) {
  const {
    useAuthenticatedData = false,
    establishmentId,
    slug,
    session,
    selectedDate,
    pastDaysOverride,
  } = options;

  const queryClient = useQueryClient();
  const { resolvedEstablishmentId } = useResolvedEstablishmentId(establishmentId, slug);

  // Pour les queries protégées (réservations authentifiées), attendre la session
  const sessionReady = !useAuthenticatedData || !!session;

  // 8 useQuery composés
  const tablesQ = useTablesQuery(
    sessionReady ? resolvedEstablishmentId : null,
    useAuthenticatedData
  );
  const combinedTablesQ = useCombinedTablesQuery(
    sessionReady ? resolvedEstablishmentId : null,
    useAuthenticatedData
  );
  const reservationsQ = useReservationsQuery({
    establishmentId: resolvedEstablishmentId,
    useAuthenticatedData,
    session,
    selectedDate,
    pastDaysOverride,
  });
  const availabilitiesQ = useAvailabilitiesQuery(
    sessionReady ? resolvedEstablishmentId : null
  );
  const weekdayAvailabilitiesQ = useWeekdayAvailabilitiesQuery(
    sessionReady ? resolvedEstablishmentId : null
  );
  const timeSlotsQ = useTimeSlotsQuery(sessionReady ? resolvedEstablishmentId : null);
  const bookablePeriodsQ = useBookablePeriodsQuery(
    sessionReady ? resolvedEstablishmentId : null
  );
  const dateOverridesQ = useDateOverridesQuery(
    sessionReady ? resolvedEstablishmentId : null,
    useAuthenticatedData
  );

  // Statut Realtime
  const [realtimeStatus, setRealtimeStatus] = useState<
    'connecting' | 'connected' | 'disconnected'
  >('connecting');

  // Realtime sur `reservations` uniquement pour le staff authentifié.
  // Les visiteurs anon n'ont plus de SELECT RLS sur la table (audit §2.5) :
  // un canal postgres_changes via supabasePublic exposerait les payloads complets.
  // Le booking public rafraîchit via refetchInterval sur reservations_availability_view.
  useEffect(() => {
    if (!useAuthenticatedData || !session || !resolvedEstablishmentId) {
      setRealtimeStatus('disconnected');
      return;
    }

    const channelName = `reservations-channel-${resolvedEstablishmentId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reservations',
          filter: `establishment_id=eq.${resolvedEstablishmentId}`,
        },
        () => {
          // Invalide UNIQUEMENT les queries reservations de cet établissement.
          // Les autres ressources (tables, time_slots…) ne sont pas touchées.
          queryClient.invalidateQueries({
            queryKey: ['reservations', resolvedEstablishmentId],
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setRealtimeStatus('connected');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setRealtimeStatus('disconnected');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [resolvedEstablishmentId, useAuthenticatedData, session, queryClient]);

  // Loading: comme avant — true tant que la 1ère vague n'a pas chargé
  const hasLoadedOnce = useRef(false);
  const allLoaded =
    !tablesQ.isLoading &&
    !combinedTablesQ.isLoading &&
    !reservationsQ.isLoading &&
    !availabilitiesQ.isLoading &&
    !weekdayAvailabilitiesQ.isLoading &&
    !timeSlotsQ.isLoading &&
    !bookablePeriodsQ.isLoading &&
    !dateOverridesQ.isLoading;

  if (allLoaded) hasLoadedOnce.current = true;
  const loading = !hasLoadedOnce.current && !allLoaded;

  // Erreur consolidée
  const error =
    (tablesQ.error as Error | null) ||
    (combinedTablesQ.error as Error | null) ||
    (reservationsQ.error as Error | null) ||
    (availabilitiesQ.error as Error | null) ||
    (weekdayAvailabilitiesQ.error as Error | null) ||
    (timeSlotsQ.error as Error | null) ||
    (bookablePeriodsQ.error as Error | null) ||
    (dateOverridesQ.error as Error | null) ||
    null;

  // refetch global → invalide toutes les queries de cet établissement
  const refetch = useCallback(async () => {
    if (!resolvedEstablishmentId) return undefined;
    const { dateFrom, dateTo } = computeReservationsDateRange(
      selectedDate,
      pastDaysOverride
    );
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: tablesQueryKey(resolvedEstablishmentId) }),
      queryClient.invalidateQueries({
        queryKey: combinedTablesQueryKey(resolvedEstablishmentId),
      }),
      queryClient.invalidateQueries({
        queryKey: reservationsQueryKey(
          resolvedEstablishmentId,
          dateFrom,
          dateTo,
          useAuthenticatedData
        ),
      }),
      queryClient.invalidateQueries({
        queryKey: availabilitiesQueryKey(resolvedEstablishmentId),
      }),
      queryClient.invalidateQueries({
        queryKey: weekdayAvailabilitiesQueryKey(resolvedEstablishmentId),
      }),
      queryClient.invalidateQueries({
        queryKey: timeSlotsQueryKey(resolvedEstablishmentId),
      }),
      queryClient.invalidateQueries({
        queryKey: bookablePeriodsQueryKey(resolvedEstablishmentId),
      }),
      queryClient.invalidateQueries({
        queryKey: dateOverridesQueryKey(resolvedEstablishmentId),
      }),
    ]);
    return undefined;
  }, [
    queryClient,
    resolvedEstablishmentId,
    selectedDate,
    pastDaysOverride,
    useAuthenticatedData,
  ]);

  // Patchers optimistes (parité avec l'ancien hook) — on patche dans le cache RQ
  const patchTable = useCallback(
    (tableId: string, updates: Partial<Table>) => {
      if (!resolvedEstablishmentId) return;
      queryClient.setQueryData<Table[] | undefined>(
        tablesQueryKey(resolvedEstablishmentId),
        (prev) =>
          prev ? prev.map((t) => (t.id === tableId ? { ...t, ...updates } : t)) : prev
      );
    },
    [queryClient, resolvedEstablishmentId]
  );

  const patchCombinedTable = useCallback(
    (combinedTableId: string, updates: Partial<CombinedTable>) => {
      if (!resolvedEstablishmentId) return;
      queryClient.setQueryData<CombinedTable[] | undefined>(
        combinedTablesQueryKey(resolvedEstablishmentId),
        (prev) =>
          prev
            ? prev.map((ct) =>
                ct.id === combinedTableId ? { ...ct, ...updates } : ct
              )
            : prev
      );
    },
    [queryClient, resolvedEstablishmentId]
  );

  // Données : on retourne des tableaux vides en l'absence de data (ne JAMAIS undefined)
  // pour préserver la compatibilité avec tous les .map/.filter en aval.
  return useMemo(
    () => ({
      tables: tablesQ.data ?? [],
      combinedTables: combinedTablesQ.data ?? [],
      reservations: reservationsQ.data ?? [],
      availabilities: availabilitiesQ.data ?? [],
      weekdayAvailabilities: weekdayAvailabilitiesQ.data ?? [],
      bookablePeriods: bookablePeriodsQ.data ?? [],
      timeSlots: timeSlotsQ.data ?? [],
      dateOverrides: dateOverridesQ.data ?? [],
      loading,
      error,
      realtimeStatus,
      refetch,
      patchTable,
      patchCombinedTable,
    }),
    [
      tablesQ.data,
      combinedTablesQ.data,
      reservationsQ.data,
      availabilitiesQ.data,
      weekdayAvailabilitiesQ.data,
      bookablePeriodsQ.data,
      timeSlotsQ.data,
      dateOverridesQ.data,
      loading,
      error,
      realtimeStatus,
      refetch,
      patchTable,
      patchCombinedTable,
    ]
  );
}
